import './IntegrationSettingsPopup.css';
import React, { useState, useRef } from 'react';
import { IntegrationFormZendesk } from 'src/components/Integrations/IntegrationForms/IntegrationFormZendesk/IntegrationFormZendesk';
import { IntegrationFormSlack } from 'src/components/Integrations/IntegrationForms/IntegrationFormSlack/IntegrationFormSlack';
import { useNotification } from 'src/hooks/useNotification';
import { useParams } from 'react-router-dom';
import { SlackIntegrationLink } from 'src/components/Integrations/IntegrationForms/IntegrationFormSlack/SlackIntegrationLink';
import { IntegrationFormFreshdesk } from 'src/components/Integrations/IntegrationForms/IntegrationFormFreshdesk/IntegrationFormFreshdesk';
import { useUpdateIntegrationMutation } from 'src/services';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { EditIcon } from 'src/assets/icons/editIcon';
import { IntegrationFormZohoSalesIqChat } from 'src/components/Integrations/IntegrationForms/IntegrationFormZohoSalesIqChat/IntegrationFormZohoSalesIqChat';
import { IntegrationFormZendeskChat } from 'src/components/Integrations/IntegrationForms/IntegrationFormZendeskChat/IntegrationFormZendeskChat';
import { IntegrationFormZendeskEmail } from 'src/components/Integrations/IntegrationForms/IntegrationFormZendeskEmail/IntegrationFormZendeskEmail';
import { IntegrationFormFreshdeskEmail } from 'src/components/Integrations/IntegrationForms/IntegrationFormFreshdeskEmail/IntegrationFormFreshdeskEmail';
import { IntegrationFormFreshdeskChat } from 'src/components/Integrations/IntegrationForms/IntegrationFormFreshdeskChat/IntegrationFormFreshdeskChat';
import { IntegrationFormZohoEmail } from 'src/components/Integrations/IntegrationForms/IntegrationFormZohoEmail/IntegrationFormZohoEmail';
import { IntegrationFormTrengo } from 'src/components/Integrations/IntegrationForms/IntegrationFormTrengo/IntegrationFormTrengo';
import { Button } from 'src/components/ui/button';
import type { ProjectIntegration } from 'src/services/projectsService';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const IntegrationSettingsPopup = ({
  integration,
  disabled = false,
  isOpen: _isOpen = false,
  trigger = (
    <div className="edit-button">
      <EditIcon />
    </div>
  ),
}: {
  integration: ProjectIntegration;
  disabled?: boolean;
  isOpen?: boolean;
  trigger?: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const notification = useNotification();
  const { id } = useParams();
  const [integrationType] = useState(integration.integration_type);
  const popupContentRef = useRef<HTMLDivElement>(null);
  const [updateIntegration, { isLoading: isLoadingUpdateIntegration }] =
    useUpdateIntegrationMutation();

  const submit = async (formData: SelectedIntegration) => {
    updateIntegration({
      projectId: id,
      integrationId: formData.id,
      body: formData,
    }).then((updateIntegrationResult) => {
      if (!('error' in updateIntegrationResult)) {
        notification.success('Integration Updated.');
        setOpen(false);
      }
    });
  };

  useLoaderWhile(isLoadingUpdateIntegration);

  const renderForm = (popupContentRef: React.RefObject<HTMLDivElement>) => {
    switch (integrationType) {
      case 'zendesk':
        return (
          <IntegrationFormZendesk
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'zendesk_chat':
        return (
          <IntegrationFormZendeskChat
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'zoho_salesiq':
        return <IntegrationFormZohoSalesIqChat integration={integration} />;
      case 'zoho_desk':
        return (
          <IntegrationFormZohoEmail
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'zendesk_email':
        return (
          <IntegrationFormZendeskEmail
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'freshdesk_email':
        return (
          <IntegrationFormFreshdeskEmail
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'freshdesk_chat':
        return (
          <IntegrationFormFreshdeskChat
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'slack':
        return (
          <IntegrationFormSlack
            popupContentRef={popupContentRef}
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'freshdesk':
        return (
          <IntegrationFormFreshdesk
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      case 'trengo':
        return (
          <IntegrationFormTrengo
            integration={integration}
            onSubmit={(formData) => submit(formData)}
          />
        );
      default:
        return null;
    }
  };

  const renderHelpContainer = () => {
    if (integrationType === 'slack') {
      return <SlackIntegrationLink link={integration.integration_data.slack_url} />;
    }
    return null;
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        {trigger}
      </DialogTrigger>
      <DialogContent className={`integration-popup ${integrationType}`}>
        <DialogHeader>
          <DialogTitle>Integration Settings</DialogTitle>
        </DialogHeader>
        <div className="content" ref={popupContentRef}>
          {integrationType && renderForm(popupContentRef)}
          {integrationType && renderHelpContainer()}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="pill-outline-dark" size="pill" style={{ width: 'auto' }}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="pill-blue"
            size="pill"
            style={{ width: 'auto' }}
            type="submit"
            form="integration-form"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
